import { QuoteExtras } from '~/lib/types/basket-types';
import useBasketSection from '~/hooks/use-basket-section';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import useQuote from '~/hooks/use-quote';
import { BasketSections } from '~/lib/types/enums';
import Upsells from '~/components/address-details/upsells/upsells';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { UpsellOptions } from '~/lib/types/extra-types';
import { isQuoteDropOff } from '~/lib/quotes';

type props = {
  limitToExtras?: string[];
  includeServiceUpgrade?: boolean;
  wrapper: (x: ReactNode) => ReactNode;
};

const Extras: FC<props> = ({ limitToExtras, wrapper, includeServiceUpgrade }) => {
  const { track } = useAnalytics();
  const { quote } = useQuote();
  const quoteIsDropOff = isQuoteDropOff(quote);
  const section = useBasketSection<QuoteExtras>({ name: BasketSections.EXTRAS });

  const extras = useMemo(() => {
    return (
      quote?.availableExtras
        .filter((extra) => UpsellOptions.includes(extra.key) && (!limitToExtras || limitToExtras.includes(extra.key)))
        .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [quote, limitToExtras]);

  const onChangeExtra = useCallback(
    (extra: string, selected: boolean) => {
      if (selected && section.data.extras.some((e) => e.key === extra)) return;
      if (selected) {
        section.update({
          ...section.data,
          extras: [...section.data.extras.filter((x) => x.key !== extra), { key: extra, metadata: {} }]
        });
      } else {
        section.update({ ...section.data, extras: section.data.extras.filter((e) => e.key !== extra) });
      }
      track({ type: 'extra_selection_changed', payload: { extra, selected } });
    },
    [section, track]
  );

  if (!quote || !extras.length) return null;

  return wrapper(
    <Upsells
      section={section}
      currency={quote.currency}
      onChange={onChangeExtra}
      extras={extras}
      quoteIsDropOff={quoteIsDropOff}
      includeServiceUpgrade={includeServiceUpgrade}
    />
  );
};

export default Extras;
